/**
 * Carga masiva de precios — productos master pendientes (Fase 2).
 * Flujo: escanear → precio → Enter → siguiente.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaBarcode, FaBolt } from 'react-icons/fa';
import productosService from '../../services/productos.service';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import CatalogActivationMetrics from '../../components/modules/productos/CatalogActivationMetrics';
import EstadoProductoMasterBadge from '../../components/modules/productos/EstadoProductoMasterBadge';
import { activarProductoMaster } from '../../services/activarProductoMaster.service';
import {
  isProductoMasterImportado,
  isProductoPendienteDePrecio,
  getMensajeActivacionProducto,
  getEstadoProductoMaster
} from '../../utils/masterCatalogProduct';

const normalizeEan = (v) => String(v || '').trim().replace(/\s/g, '');

const ActivacionMasivaPendientes = () => {
  const { sucursalSeleccionada, orgId } = useAuth();
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [scanBuffer, setScanBuffer] = useState('');
  const [current, setCurrent] = useState(null);
  const [precio, setPrecio] = useState('');
  const [stockQty, setStockQty] = useState('10');
  const [cargarStock, setCargarStock] = useState(true);

  const scanRef = useRef(null);
  const precioRef = useRef(null);
  const scanDebounceRef = useRef(null);

  const pendientes = useMemo(
    () => productos.filter((p) => isProductoMasterImportado(p) && isProductoPendienteDePrecio(p)),
    [productos]
  );

  const enfocarEscaneo = useCallback(() => {
    setTimeout(() => scanRef.current?.focus(), 50);
  }, []);

  const cargarProductos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await productosService.obtenerTodos();
      const list = Array.isArray(response) ? response : (response?.data || []);
      setProductos(list);
    } catch (err) {
      console.error(err);
      toast.error('No se pudieron cargar los productos');
    } finally {
      setLoading(false);
      enfocarEscaneo();
    }
  }, [enfocarEscaneo]);

  useEffect(() => {
    cargarProductos();
  }, [cargarProductos]);

  const seleccionarPorEan = useCallback((ean) => {
    const code = normalizeEan(ean);
    if (!code) return false;
    const found = pendientes.find((p) => {
      const cb = normalizeEan(p.codigo_barras || p.codigo || '');
      return cb === code || cb.endsWith(code) || code.endsWith(cb);
    });
    if (!found) {
      toast.warning(`No hay pendiente con EAN ${code}`);
      enfocarEscaneo();
      return false;
    }
    setCurrent(found);
    setPrecio('');
    setTimeout(() => precioRef.current?.focus(), 60);
    return true;
  }, [pendientes, enfocarEscaneo]);

  const avanzarSiguiente = useCallback((excluirId) => {
    const rest = pendientes.filter((p) => p.id !== excluirId);
    if (rest.length > 0) {
      setCurrent(rest[0]);
      setPrecio('');
      setTimeout(() => precioRef.current?.focus(), 60);
    } else {
      setCurrent(null);
      setPrecio('');
      toast.success('¡Todos los pendientes fueron activados!');
      enfocarEscaneo();
    }
  }, [pendientes, enfocarEscaneo]);

  const guardarActual = async () => {
    if (!current) {
      toast.info('Escaneá o elegí un producto pendiente');
      enfocarEscaneo();
      return;
    }
    const precioNum = parseFloat(String(precio).replace(',', '.'));
    if (Number.isNaN(precioNum) || precioNum <= 0) {
      toast.error('Ingresá un precio mayor a 0');
      precioRef.current?.focus();
      return;
    }
    if (!sucursalSeleccionada?.id) {
      toast.error('Seleccioná una sucursal');
      return;
    }

    try {
      setGuardando(true);
      const updated = await activarProductoMaster({
        productoId: current.id,
        precioVenta: precioNum,
        sucursalId: sucursalSeleccionada.id,
        orgId,
        cargarStock,
        cantidadStock: cargarStock ? parseInt(stockQty, 10) || 0 : 0
      });
      const estado = getEstadoProductoMaster(updated, updated.stock_sucursal);
      toast.success(getMensajeActivacionProducto(estado), { autoClose: 1500 });
      setProductos((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
      avanzarSiguiente(current.id);
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const handleScanKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const code = normalizeEan(scanBuffer);
      setScanBuffer('');
      if (code) seleccionarPorEan(code);
      return;
    }
    if (e.key.length === 1) return;
  };

  const handleScanChange = (e) => {
    const val = e.target.value;
    setScanBuffer(val);
    if (scanDebounceRef.current) clearTimeout(scanDebounceRef.current);
    scanDebounceRef.current = setTimeout(() => {
      const code = normalizeEan(val);
      if (code.length >= 8) {
        seleccionarPorEan(code);
        setScanBuffer('');
      }
    }, 120);
  };

  const handlePrecioKeyDown = (e) => {
    if (e.key === 'Enter' && !guardando) {
      e.preventDefault();
      guardarActual();
    }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Link to="/productos" className="text-slate-600 hover:text-slate-900">
          <FaArrowLeft />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FaBolt className="text-orange-500" />
            Carga masiva — pendientes de precio
          </h1>
          <p className="text-sm text-slate-600">
            Escaneá → precio → Enter. Sin formularios completos.
          </p>
        </div>
      </div>

      <CatalogActivationMetrics productos={productos} showBulkLink={false} />

      <Card>
        <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">
          <FaBarcode className="inline mr-1" />
          Escáner / EAN
        </label>
        <input
          ref={scanRef}
          type="text"
          className="nexo-field w-full font-mono text-lg"
          value={scanBuffer}
          onChange={handleScanChange}
          onKeyDown={handleScanKeyDown}
          placeholder="Pase el código de barras..."
          autoComplete="off"
          disabled={guardando}
        />
        <p className="text-xs text-slate-500 mt-1">
          {pendientes.length} pendientes · Enter confirma escaneo · En precio, Enter guarda y avanza
        </p>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : pendientes.length === 0 ? (
        <Card>
          <p className="text-center text-green-700 font-medium py-8">
            No hay productos pendientes de precio en el catálogo importado.
          </p>
          <div className="text-center">
            <Link to="/productos">
              <Button color="secondary">Volver a productos</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-3">
              <span className="text-xs font-medium text-slate-500">EAN</span>
              <p className="font-mono text-sm mt-1">
                {current?.codigo_barras || current?.codigo || '—'}
              </p>
            </div>
            <div className="md:col-span-4">
              <span className="text-xs font-medium text-slate-500">Descripción</span>
              <p className="text-sm font-medium mt-1 line-clamp-2">
                {current?.nombre || 'Escaneá un producto'}
              </p>
              {current && (
                <div className="mt-1">
                  <EstadoProductoMasterBadge producto={current} size="xs" />
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-slate-500">Precio *</label>
              <input
                ref={precioRef}
                type="number"
                min="0"
                step="0.01"
                className="nexo-field w-full mt-1"
                value={precio}
                onChange={(e) => setPrecio(e.target.value)}
                onKeyDown={handlePrecioKeyDown}
                placeholder="0"
                disabled={!current || guardando}
              />
            </div>
            <div className="md:col-span-3">
              <label className="flex items-center gap-2 text-xs mb-1">
                <input
                  type="checkbox"
                  checked={cargarStock}
                  onChange={(e) => setCargarStock(e.target.checked)}
                  disabled={guardando}
                />
                Stock inicial
              </label>
              <input
                type="number"
                min="0"
                className="nexo-field w-full"
                value={stockQty}
                onChange={(e) => setStockQty(e.target.value)}
                disabled={!cargarStock || guardando}
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              color="primary"
              className="flex-1"
              onClick={guardarActual}
              disabled={!current || guardando}
            >
              {guardando ? <Spinner size="sm" /> : 'Guardar (Enter)'}
            </Button>
            <Button
              color="secondary"
              onClick={() => avanzarSiguiente(current?.id)}
              disabled={guardando}
            >
              Saltar
            </Button>
          </div>
        </Card>
      )}

      {pendientes.length > 0 && pendientes.length <= 50 && (
        <Card title="Cola pendiente">
          <ul className="max-h-48 overflow-y-auto text-sm divide-y">
            {pendientes.slice(0, 30).map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={`w-full text-left py-2 px-1 hover:bg-slate-50 ${current?.id === p.id ? 'bg-orange-50' : ''}`}
                  onClick={() => {
                    setCurrent(p);
                    setPrecio('');
                    precioRef.current?.focus();
                  }}
                >
                  <span className="font-mono text-xs text-slate-500">{p.codigo_barras || p.codigo}</span>
                  <span className="ml-2">{p.nombre}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

export default ActivacionMasivaPendientes;
